import { Stack, Typography } from '@mui/material'
import { FC, MouseEventHandler } from 'react'
import { Button } from 'utoniq-ui'

import {
  GachaTicketConnectionFragment,
  GpsData,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { HeaderDisplay, PointHeaderDisplay } from '@/components/organisms'
import { useTranslation } from '@/core'
import { Cell } from '@/core/architecture'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'
import { usePrivateAction } from '@/lib/privateAction'

type Props = {
  loading: boolean
  isAccessible: boolean
  isSoldOut: boolean
  isLimited: boolean
  price?: number
  remainingLimitedItemCount: number
  gachaTicket: Maybe<GachaTicketConnectionFragment>
  gpsGachaData?: Maybe<
    Pick<GpsData, 'radius' | 'approximateAddress' | 'latitude' | 'longitude'>
  >

  isPointOnly: boolean
  isInside: boolean
  isShowModalPersonalInfoRequire: boolean
  onClickTrigger: (count: number) => Promise<void>
}

/**
 * @description
 * ガチャを回すボタン[単発, 10連]
 */
export const TriggerButtons: Cell<Props> = ({
  loading,
  isAccessible,
  isSoldOut,
  isLimited,
  onClickTrigger,
  gachaTicket,
  gpsGachaData,
  price,
  remainingLimitedItemCount,
  isPointOnly = false,
  isInside,
  isShowModalPersonalInfoRequire,
}) => {
  const { t } = useTranslation('gachaDetail')
  const { showModal: showUserInfoModal } = useSettingUserInfoModal()
  const { handleLoginModal } = usePrivateAction()

  return (
    <Stack id="gacha-draw-area" direction="row" spacing={5}>
      {
        /**
         * 売り切れ状態の表示
         */
        isSoldOut && (
          <Button
            fullWidth
            disabled
            sx={{ bgcolor: 'grey.100', height: 72, borderRadius: 2 }}
          >
            <Typography variant="h5" color="grey.400">
              {t('soldOutBtn')}
            </Typography>
          </Button>
        )
      }
      {
        /**
         * 売り切れじゃないとき
         */
        !isSoldOut &&
          [1, 10].map((number, index) => {
            /**
             * ポイントでしか引けないのガチャの場合
             */
            if (isPointOnly) {
              const requirePoint = (price ?? 0) * number

              return (
                <GachaDrawButtonBase
                  key={index}
                  disabled={
                    !isAccessible ||
                    // ローディング中か
                    loading ||
                    // GPSガチャ且つ、範囲内にいるとき
                    (!!gpsGachaData && !isInside) ||
                    // 限定ガチャ且つ、引く個数分の在庫があるとき
                    (isLimited && remainingLimitedItemCount < number)
                  }
                  onClick={handleLoginModal(() => {
                    isShowModalPersonalInfoRequire
                      ? showUserInfoModal()
                      : onClickTrigger(number)
                  })}
                >
                  <Typography variant="h5" color="inherit">
                    {t('pullAround', { number })}
                  </Typography>
                  <PointHeaderDisplay point={requirePoint} />
                </GachaDrawButtonBase>
              )
            }

            /**
             * ガチャチケット・ポイント両方で引けるガチャの場合
             */
            return (
              <GachaDrawButtonBase
                key={index}
                disabled={
                  !isAccessible ||
                  // ローディング中か
                  loading ||
                  // GPSガチャ且つ、範囲内にいるとき
                  (!!gpsGachaData && !isInside) ||
                  // 限定ガチャ且つ、引く個数分の在庫があるとき
                  (isLimited && remainingLimitedItemCount < number)
                }
                onClick={handleLoginModal(() => {
                  isShowModalPersonalInfoRequire
                    ? showUserInfoModal()
                    : onClickTrigger(number)
                })}
              >
                <Typography variant="h5" color="inherit">
                  {t('pullAround', { number })}
                </Typography>
                <HeaderDisplay
                  src={gachaTicket?.thumbnails?.standard?.mediumUri ?? ''}
                  ticket={number}
                />
              </GachaDrawButtonBase>
            )
          })
      }
    </Stack>
  )
}

/**
 * @description
 * 「ガチャを引く」ボタン用の共通ベースコンポーネント
 */
const GachaDrawButtonBase: FC<{
  disabled: boolean
  onClick: MouseEventHandler<HTMLButtonElement>
}> = ({ disabled, onClick, children }) => {
  return (
    <Button
      disabled={disabled}
      fullWidth
      onClick={onClick}
      variant="contained"
      sx={{
        height: 72,
        borderRadius: 2,
        flexDirection: 'column',
        color: 'white',
        '&.Mui-disabled .MuiTypography-root': {
          color: 'text.disabled',
        },
        '&.Mui-disabled>div': {
          bgcolor: 'white',
        },
        '&.Mui-disabled img': {
          filter: 'grayscale(95%) opacity(.55)',
        },
        '&.Mui-disabled svg.MuiSvgIcon-root': {
          filter: 'grayscale(95%)',
        },
      }}
    >
      {children}
    </Button>
  )
}
